
/**
 * This module contains UI widgets specific to dealing with IOs
 * and pneumatics.
 */

(function() {
  const $ = require('jQuery');
  const widgets = require('../widgets');
  const ml = require('../../machinelogic');

  /**
   * Helper function to create an IO option HTML element.
   *
   * @param {Input | Output} pIo
   * @returns {JQuery} Option object
   */
  function createIoOption(pIo) {
    return $('<option value="' + pIo.getUUID() + '">' + pIo.getName() + '</option>');
  }

  /**
   * Helper function to create an array of options from an actuator.
   *
   * @param {Axis} pAxis
   * @returns {JQuery[]} List of options
   */
  function createPneumaticOption(pAxis) {
    // Adding ReedSwitch from pneumatics as an option. Please note that the value
    // of a pneumatic input here will be UUID_(push | pull). This value will be
    // processed at run time by the execution engine.
    const lPushAction = pAxis.getType() === "pneumatic_bag_gripper" ? "Close" : "Push";
    const lPullAction = pAxis.getType() === "pneumatic_bag_gripper" ? "Open" : "Pull";

    const lPullTitle = `${pAxis.getName()} ${lPullAction}`;
    const lPushTitle = `${pAxis.getName()} ${lPushAction}`;
    const lPullValue = pAxis.getUUID() + '_pull';
    const lPushValue = pAxis.getUUID() + '_push';
    const lPushOption = $('<option value="' + lPushValue + '">' + lPushTitle + '</option>');
    const lPullOption = $('<option value="' + lPullValue + '">' + lPullTitle + '</option>');
    const lOptionsList = [];

    if (pAxis.getPin('pushInPin') != null) { lOptionsList.push(lPushOption); }
    if (pAxis.getPin('pullInPin') != null) { lOptionsList.push(lPullOption); }

    return lOptionsList;
  }

  function baseIoWidget(pName, pTooltip, pEmptyAllowed, pValue, pOnValue) {
    // NOTE: baseWidget will use camelCaseToText to transform the name, we dont want that as we already
    // give it converted name and it would introduce extra spaces that we dont want (for searching, errro-handling)
    const lWidget = widgets.baseWidget(pName, function(pText) { return pText; });
    const lIOWidget = widgets.select(pTooltip).css({ flex: 1 });
    const lIoSelect = lIOWidget.getSelect();
    const lGetOptionsArray = function (pIoSelect) {
      return $.map(pIoSelect[0], function (option) {
        return (option.value || option.text);
      });
    };
    const lSetIoValue = function (pVal) {
      if (pOnValue) {
        lIoSelect.find('option[value="' + ml.reservedUuids.UNKNOWN + '"],option[disabled="disabled"]').remove(); // remove (Not Configured) if it is present
        pOnValue(pVal);
      }

      lIoSelect.val(pVal);
    };

    lIoSelect.on('change', function (pEvent) {
      pEvent.stopPropagation();
      lSetIoValue(lIoSelect.prop("value") == 'null' ? null : lIoSelect.prop("value"));
    });

    if (pEmptyAllowed) {
      lIoSelect.append($('<option>', { value: null, text: '(Not selected)' }));
    }

    lWidget.createConfigGroup = function(pConfig) {
      const lOptGroup = $('<optgroup>').attr('label', pConfig.getController().getName()).appendTo(lIoSelect);

      return {
        appendIo: function(pIo) {
          lOptGroup.append(createIoOption(pIo));
        },
        appendPneumatic: function(pAxis) {
          lOptGroup.append(createPneumaticOption(pAxis));
        }
      };
    };

    lWidget.initialize = function() {
      switch (pValue) {
        case ml.reservedUuids.ANY: {
          lSetIoValue(lGetOptionsArray(lIoSelect)[0]);
          break;
        }
        case ml.reservedUuids.NONE: {
          lSetIoValue(null);
          break;
        }
        default: {
          const _lOptionsArray = lGetOptionsArray(lIoSelect);
          if (!_lOptionsArray.includes(pValue)) {
            lIoSelect.append($('<option value="' + ml.reservedUuids.UNKNOWN + '">(Not Configured)</option>').attr('disabled', 'disabled'));
            lIoSelect.val(ml.reservedUuids.UNKNOWN);
            lIoSelect.addClass('ml_validator_red');
            // This is to remove the RED on invalid option when the drop down is openeded
            // NOTE: so apparently, lIoSelect.val() will be null if the option is disabled... that is unfortunate....
            lIoSelect.on('click', function() { lIoSelect.removeClass('ml_validator_red'); });
            lIoSelect.on('focusout', function() { if(lIoSelect[0].value == ml.reservedUuids.UNKNOWN) { lIoSelect.addClass('ml_validator_red'); } });
            lIoSelect.on('keydown', function(pEvent) { if(pEvent.keyCode == 40) { lIoSelect.removeClass('ml_validator_red'); } });
          } else {
            lIoSelect.val(pValue);
          }
        }
      }
    };

    lWidget.append(lIOWidget);
    return lWidget;
  }

  /**
   * Creates an input selector widget.
   *
   * @param {ConfigManager} pConfigManager
   * @param {String} pName Display name of the parameter.
   * @param {String} pTooltip Display tooltip of the parameter.
   * @param {Boolean} pEmptyAllowed Whether or not null is available as a selection.
   * @param {String} pValue Selected Input unique ID.
   * @param {Function<String>} pOnValue Callback when a new Input unique ID is selected.
   * @param {Boolean} pAllowPneumatics Specifies whether nor not pneumatics are allowed as options.
   *
   * @returns {JQuery} Widget Element
   */
  function inputParamWidget(pConfigManager, pName, pTooltip, pEmptyAllowed, pValue, pOnValue, pAllowPneumatics) {
    const lWidget = baseIoWidget(pName, pTooltip, pEmptyAllowed, pValue, pOnValue);

    pConfigManager.getConfigurationList().forEach(function(pConfig) {
      const lConfigGroup = lWidget.createConfigGroup(pConfig);
      pConfig.listInputs().forEach(function(pInput) {
        lConfigGroup.appendIo(pInput);
      });

      if (pAllowPneumatics) {
        pConfig.listAxes().forEach(function (lAxis) {
          if (lAxis.isNonMotorized()) {
            lConfigGroup.appendPneumatic(lAxis);
          }
        });
      }
    });

    lWidget.initialize();
    return lWidget;
  }

  /**
   * Creates an output selector widget.
   *
   * @param {ConfigManager} pConfigManager
   * @param {String} pName Display name of the parameter.
   * @param {String} pTooltip Display tooltip of the parameter.
   * @param {Boolean} pEmptyAllowed Whether or not null is available as a selection.
   * @param {String} pValue Selected Output unique ID.
   * @param {Function<String>} pOnValue Callback when a new Output unique ID is selected.
   *
   * @returns {JQuery} Widget Element
   */
  function outputParamWidget(pConfigManager, pName, pTooltip, pEmptyAllowed, pValue, pOnValue) {
    const lWidget = baseIoWidget(pName, pTooltip, pEmptyAllowed, pValue, pOnValue);

    pConfigManager.getConfigurationList().forEach(function(pConfig) {
      const lConfigGroup = lWidget.createConfigGroup(pConfig);
      pConfig.listOutputs().forEach(function(pOutput) {
        lConfigGroup.appendIo(pOutput);
      });
    });

    lWidget.initialize();
    return lWidget;
  }

  /* Module export glue */
  /* global define */
  const gExports = {
    inputParamWidget: inputParamWidget,
    outputParamWidget: outputParamWidget
  };
  const gModule = typeof module;
  if (gModule !== 'undefined' && module.exports != undefined) {
    module.exports = gExports;
  } else if (define != undefined && typeof define === 'function' && define.amd) {
    define([], function () { return gExports; });
  }
})();
